import React, { useState, useEffect, useRef } from 'react';
import {
  Modal,
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  FormHelperText
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import axios from 'axios';

// ==================== COUNTRY CODES DATA ====================
let COUNTRY_CODES = [];

// ==================== COUNTRY TO CODE MAPPING ====================
let COUNTRY_NAME_TO_CODE_MAP = {};

// ==================== SEARCHABLE SELECT COMPONENT ====================
const SearchableSelect = ({
  value,
  options,
  onChange,
  onOpen,
  placeholder = "Select...",
  label,
  searchPlaceholder = "Search...",
  disabled = false,
  getOptionLabel = (option) => option?.name || '',
  getOptionValue = (option) => option?.id || ''
}) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState([]);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (searchTerm) {
      const filtered = options.filter(option =>
        getOptionLabel(option).toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredOptions(filtered);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    if (disabled) return;
    setOpen(true);
    setSearchTerm('');
    if (onOpen) onOpen();
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleSelect = (option) => {
    onChange(option);
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm('');
  };

  const selectedLabel = value ? getOptionLabel(value) : '';

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      {label && (
        <label style={{
          display: 'block',
          marginBottom: '6px',
          fontSize: '14px',
          fontWeight: 500,
          color: '#374151'
        }}>
          {label}
        </label>
      )}

      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? searchTerm : selectedLabel}
          onChange={(e) => {
            if (open) {
              setSearchTerm(e.target.value);
            }
          }}
          onClick={handleOpen}
          placeholder={open ? searchPlaceholder : placeholder}
          disabled={disabled}
          readOnly={!open}
          style={{
            width: '100%',
            padding: '10px 40px 10px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '14px',
            outline: 'none',
            cursor: open ? 'text' : 'pointer',
            backgroundColor: disabled ? '#f3f4f6' : 'white',
            transition: 'all 0.2s',
            ...(open && {
              borderColor: '#ff3b30',
              boxShadow: '0 0 0 3px rgba(255, 59, 48, 0.1)'
            })
          }}
        />

        {/* Clear button */}
        {value && !open && !disabled && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '32px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#ff3b30';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#9ca3af';
            }}
            type="button"
          >
            <svg
              style={{
                width: '16px',
                height: '16px',
                pointerEvents: 'none'
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {/* Dropdown arrow */}
        <svg
          style={{
            position: 'absolute',
            right: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '20px',
            height: '20px',
            color: open ? '#ff3b30' : '#9ca3af',
            pointerEvents: 'none'
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          {open ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          )}
        </svg>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          maxHeight: '400px',
          overflow: 'auto',
          zIndex: 1000,
          marginTop: '4px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{
              padding: '12px 16px',
              color: '#6b7280',
              fontSize: '14px'
            }}>
              No results found
            </div>
          ) : (
            filteredOptions.map((option, index) => {
              const optionValue = getOptionValue(option);
              const isSelected = value && getOptionValue(value) === optionValue;

              return (
                <div
                  key={optionValue || index}
                  onClick={() => handleSelect(option)}
                  style={{
                    padding: '12px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 500,
                    transition: 'all 0.2s',
                    backgroundColor: isSelected ? 'rgba(255, 59, 48, 0.12)' : 'white',
                    color: isSelected ? '#ff3b30' : '#374151',
                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgba(255, 59, 48, 0.08)';
                    e.currentTarget.style.paddingLeft = '20px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(255, 59, 48, 0.12)' : 'white';
                    e.currentTarget.style.paddingLeft = '16px';
                  }}
                >
                  {getOptionLabel(option)}
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

// ==================== COUNTRY CODE SELECT COMPONENT ====================
// selectedId: countryId se exact country (US/Canada dono +1 hain)
const CountryCodeSelect = ({ value, selectedId, onChange, countryName, isLoggedIn = false, options = COUNTRY_CODES }) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredOptions, setFilteredOptions] = useState(options);
  const inputRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (searchTerm) {
      const term = searchTerm.trim().toLowerCase();
      const filtered = options.filter(item =>
        (item.country || '').toLowerCase().includes(term) ||
        (item.code || '').toLowerCase().includes(term) ||
        (item.iso || '').toLowerCase().includes(term)
      );
      // Prioritize country names that start with the search term over those
      // that merely contain it somewhere in the middle (e.g. "in" -> India
      // before Bahrain), keeping the original order within each group.
      // Exact ISO match sabse upar (e.g. "in" -> India, "cl" -> Chile)
      const isoMatch = [];
      const startsWith = [];
      const containsOnly = [];
      filtered.forEach(item => {
        if ((item.iso || '').toLowerCase() === term) {
          isoMatch.push(item);
        } else if ((item.country || '').toLowerCase().startsWith(term)) {
          startsWith.push(item);
        } else {
          containsOnly.push(item);
        }
      });
      setFilteredOptions([...isoMatch, ...startsWith, ...containsOnly]);
    } else {
      setFilteredOptions(options);
    }
  }, [searchTerm, options]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open]);

  const handleOpen = () => {
    setOpen(true);
    setSearchTerm('');
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  const handleClose = () => {
    setOpen(false);
    setSearchTerm('');
  };

  const handleSelect = (item) => {
    onChange(item.code, item);
    handleClose();
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('', null);
    setSearchTerm('');
  };

  // Pehle countryId se, na mile to dial code se
  const selectedItem =
    (selectedId && options.find(item => item.countryId === selectedId)) ||
    options.find(item => item.code === value);

  return (
    <div ref={containerRef} style={{ position: 'relative', width: '100%' }}>
      <div style={{ position: 'relative' }}>
        <input
          ref={inputRef}
          type="text"
          value={open ? searchTerm : (selectedItem ? `${(selectedItem.iso || '').toUpperCase()} ${selectedItem.code}`.trim() : '')}
          onChange={(e) => {
            if (open) {
              setSearchTerm(e.target.value);
            }
          }}
          onClick={handleOpen}
          placeholder="Search country code..."
          readOnly={!open}
          style={{
            width: '100%',
            padding: '8px 40px 8px 12px',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            fontSize: '13px',
            outline: 'none',
            cursor: open ? 'text' : 'pointer',
            backgroundColor: 'white',
            transition: 'all 0.2s',
            ...(open && {
              borderColor: '#ff3b30',
              boxShadow: '0 0 0 3px rgba(255, 59, 48, 0.1)'
            })
          }}
        />

        {/* Clear button */}
        {value && !open && (
          <button
            onClick={handleClear}
            style={{
              position: 'absolute',
              right: '15px',
              top: '50%',
              transform: 'translateY(-50%)',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#9ca3af',
              transition: 'color 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = '#ff3b30';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = '#9ca3af';
            }}
            type="button"
          >
            <svg
              style={{
                width: '14px',
                height: '14px',
                pointerEvents: 'none'
              }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}

        {/* Dropdown arrow */}
        <svg
          style={{
            position: 'absolute',
            right: '1px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '16px',
            height: '16px',
            color: open ? '#ff3b30' : '#9ca3af',
            pointerEvents: 'none'
          }}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 26 26"
        >
          {open ? (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          )}
        </svg>
      </div>

      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 0,
          maxHeight: '295px',
          width: '110%',
          overflow: 'auto',
          zIndex: 1000,
          marginTop: '4px',
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          Width: '5px',
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
        }}>
          {filteredOptions.length === 0 ? (
            <div style={{
              padding: '12px 16px',
              color: '#6b7280',
              fontSize: '10px'
            }}>
              No results found
            </div>
          ) : (
            filteredOptions.map((item, index) => {
              const isSelected = !!selectedItem && item === selectedItem;
              const isCountryMatch = countryName && item.country.toLowerCase().includes(countryName.toLowerCase());

              return (
                <div
                  key={`${item.code}-${index}`}
                  onClick={() => handleSelect(item)}
                  style={{
                    padding: '10px 16px',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: 500,
                    transition: 'all 0.2s',
                    backgroundColor: isSelected ? 'rgba(255, 59, 48, 0.12)' : (isCountryMatch && !isLoggedIn ? 'rgba(0, 150, 255, 0.08)' : 'white'),
                    color: isSelected ? '#ff3b30' : (isCountryMatch && !isLoggedIn ? 'rgba(226, 109, 41, 1)' : '#374151'),
                    borderBottom: index < filteredOptions.length - 1 ? '1px solid #f3f4f6' : 'none',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(255, 59, 48, 0.2)' : (isCountryMatch && !isLoggedIn ? 'rgba(0, 150, 255, 0.15)' : 'rgba(255, 59, 48, 0.08)');
                    e.currentTarget.style.paddingLeft = '20px';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = isSelected ? 'rgba(255, 59, 48, 0.12)' : (isCountryMatch && !isLoggedIn ? 'rgba(0, 150, 255, 0.08)' : 'white');
                    e.currentTarget.style.paddingLeft = '16px';
                  }}
                >
                  {/* ISO code capital me (Admin Profile jaisa) - Windows pe flag emoji letters jaise dikhte the */}
                  <span style={{ fontSize: '13px', fontWeight: 600, minWidth: '28px', color: '#111827' }}>{(item.iso || '').toUpperCase()}</span>
                  <span>{item.code}</span>
                  <span style={{ marginLeft: 'auto', color: (isCountryMatch && !isLoggedIn) ? '#c82905ff' : '#6b7280', fontSize: '12px' }}>
                    {item.country}
                    {(isCountryMatch && !isLoggedIn) && ' (Suggested)'}
                    {(isSelected && isLoggedIn) && ' (Your registered code)'}
                  </span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

const InquiryModal = ({ open, onClose, product, imageUrl1 }) => {
  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    emailId: "",
    phoneNumber: "",
    description: "",
    country: null,
    port: null,
    userMessage: '',
    shippingOption: '',
    shippingType: null,
    containerType: null,
    containerShippingOption: null,
    countryCode: '',
    countryCodeId: null, // selected phone country ka id (US vs Canada)
    guestCountryCode: '', // Added for payload like code 1
    shippingCalculation: null
  });

  // UI state
  const [inquiryType, setInquiryType] = useState('general');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [openSpecifications, setOpenSpecifications] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [isUserLoggedIn, setIsUserLoggedIn] = useState(false);
  const [countryCodes, setCountryCodes] = useState([]);

  // Data state
  const [countriesList, setCountriesList] = useState([]);
  const [portsList, setPortsList] = useState([]);
  const [userDetails, setUserDetails] = useState(null);

  // Constants
  const labelMap = {
    firstName: 'First Name',
    lastName: 'Last Name',
    phoneNumber: 'Phone Number',
    emailId: 'Email Address',
    userMessage: 'Your Message'
  };

  const shippingOptions = [
    { value: "FOB", label: "FOB (Free On Board)" },
    { value: "CNF", label: "C&F (Cost and Freight)" },
    { value: "CIF", label: "CIF (Cost, Insurance and Freight)" },
  ];

  // Helper function to get header title based on shipping type and container type
 const getHeaderTitle = () => {
  // For container shipping
  if (formData.shippingType === 'container' && formData.containerType && formData.containerShippingOption) {
    const containerSize = formData.containerType === '20ft' ? '20ft' : '40ft';
    const shippingOption = formData.containerShippingOption;
    
    if (shippingOption === "CIF") {
      return `${containerSize} Container CIF Cost`;
    } else if (shippingOption === "CNF") {
      return `${containerSize} Container C&F Cost`;
    } else if (shippingOption === "FOB") {
      return `${containerSize} Container FOB Cost`;
    }
    return `${containerSize} Container Total Cost`;
  }

  // For individual shipping
  if (formData.shippingType === 'individual') {
    if (formData.shippingOption === "CIF") {
      return "Total CIF Cost";
    } else if (formData.shippingOption === "CNF") {
      return "Total C&F Cost";
    } else if (formData.shippingOption === "FOB") {
      return "Total FOB Cost";
    }
  }

  // Default
  return "Total Cost";
};

  // Helper function to get cost summary title
  const getCostSummaryTitle = () => {
    // For container shipping
    if (formData.shippingType === 'container' && formData.containerType && formData.containerShippingOption) {
      const containerSize = formData.containerType === '20ft' ? '20ft' : '40ft';
      const shippingOption = formData.containerShippingOption;
      
      if (shippingOption === "CIF") {
        return `${containerSize} Container CIF Cost`;
      } else if (shippingOption === "CNF") {
        return `${containerSize} Container C&F Cost`;
      } else if (shippingOption === "FOB") {
        return `${containerSize} Container FOB Cost`;
      }
      return `${containerSize} Container Total Cost`;
    }

    // For individual shipping
    if (formData.shippingType === 'individual') {
      if (formData.shippingOption === "CIF") {
        return "Total CIF Cost";
      } else if (formData.shippingOption === "CNF") {
        return "Total C&F Cost";
      } else if (formData.shippingOption === "FOB") {
        return "Total FOB Cost";
      }
    }

    // Default
    return "Total Cost";
  };

  // Helper function to get shipping type for payload like code 1
  const getShippingTypeForPayload = () => {
    if (formData.shippingType === 'container') {
      if (formData.containerType === '20ft') {
        return '20ft container';
      } else if (formData.containerType === '40ft') {
        return '40ft container';
      }
    }
    return 'individual shippment';
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await fetch(
          "https://car.amar.co.jp/api/authorization/shipping/all-countries",
          { headers: { "Accept-Language": "en" } }
        );
        const rawResponse = await res.json();
        // Handle either a bare array response, or one wrapped in an envelope
        // object (e.g. { data: [...] } / { content: [...] } / { countries: [...] }).
        const data = Array.isArray(rawResponse)
          ? rawResponse
          : (rawResponse?.data || rawResponse?.content || rawResponse?.countries || []);
        const isoToFlag = (iso) => {
          if (!iso || typeof iso !== 'string' || iso.length !== 2) return '🏳️';
          return iso.toUpperCase().replace(/./g, (c) =>
            String.fromCodePoint(c.charCodeAt(0) + 127397)
          );
        };
        COUNTRY_CODES = data.map((c) => ({
          code: c.countryPhoneCode,
          country: c.countryName,
          flag: isoToFlag(c.isoCode),
          iso: c.isoCode || '', // ISO se search ke liye (e.g. "IN", "CL") - null-safe
          countryId: c.countryId, // US/Canada dono +1 - exact country id se pehchano
        }));
        data.forEach((c) => {
          COUNTRY_NAME_TO_CODE_MAP[c.countryName] = c.countryPhoneCode;
        });
        setCountryCodes([...COUNTRY_CODES]);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  // Fetch user details when modal opens
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token && open) {
      setIsUserLoggedIn(true);
      fetchUserDetails(token);
    } else {
      setIsUserLoggedIn(false);
      setFormData(prev => ({
        ...prev,
        countryCode: '',
        countryCodeId: null,
        guestCountryCode: ''
      }));
    }
  }, [open]);

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      resetFormData();
    }
  }, [open]);

  // Match the user's registered country code once BOTH the countries list and the
  // user's details have loaded. fetchCountries() and fetchUserDetails() run in
  // parallel, so whichever finishes last decides when this can safely run —
  // matching inside fetchUserDetails alone could race ahead of the countries list.
  useEffect(() => {
    if (!userDetails || countryCodes.length === 0) return;
    const userCountryCode = userDetails.countryCode || '';
    if (!userCountryCode) return;
    const normalize = (code) => (code || '').replace(/\D/g, ''); // digits only, ignores '+' formatting differences
    // Profile ka countryId pehle (US vs Canada), na ho to dial code se
    const matchedCode =
      (userDetails.countryId && countryCodes.find(item => item.countryId === userDetails.countryId)) ||
      countryCodes.find(item => normalize(item.code) === normalize(userCountryCode));
    if (matchedCode) {
      setFormData(prev => ({
        ...prev,
        countryCode: matchedCode.code,
        countryCodeId: matchedCode.countryId || null,
        guestCountryCode: matchedCode.code
      }));
    }
  }, [userDetails, countryCodes]);

  // Recalculate price when port changes and shipping option is selected
  useEffect(() => {
    if (formData.port && (formData.shippingOption || formData.containerShippingOption)) {
      recalculateShippingPrice();
    }
  }, [formData.port]);

  // Fetch ports when country changes
  useEffect(() => {
    if (formData.country) {
      fetchPorts(formData.country.countryId);
    }
  }, [formData.country]);

  const fetchUserDetails = async (token) => {
    try {
      const response = await axios.get('https://car.amar.co.jp/api/authorization/user/get/user/details', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.code === 200) {
        setUserDetails(response.data.userDetails);

        const userPhoneNumber = response.data.userDetails.userMobileNumber || '';
        const phoneDigits = userPhoneNumber.replace(/\D/g, '');

        setFormData(prev => ({
          ...prev,
          firstName: response.data.userDetails.firstName || '',
          lastName: response.data.userDetails.lastName || '',
          phoneNumber: phoneDigits.slice(0, 15) || '',
          emailId: response.data.userDetails.email || '',
          // countryCode / guestCountryCode are matched separately below, once the
          // country codes list has loaded — fetchCountries() and fetchUserDetails()
          // run in parallel, so matching here immediately can race and fail if
          // this resolves first.
        }));
      }
    } catch (error) {
      console.error('Failed to fetch user details:', error);
      localStorage.removeItem('authToken');
      setUserDetails(null);
      setIsUserLoggedIn(false);
    }
  };

  const fetchCountries = async () => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/countries');
      const data = await response.json();
      setCountriesList(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchPorts = async (countryId) => {
    try {
      const response = await fetch(`https://car.amar.co.jp/api/authorization/shipping/ports/country/${countryId}`);
      const data = await response.json();
      setPortsList(data);
    } catch (error) {
      console.error('Error fetching ports:', error);
    }
  };

  const calculateShippingPrice = async (data) => {
    try {
      const response = await fetch('https://car.amar.co.jp/api/authorization/shipping/calculate-price', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(data)
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Handle 400 error with volume exceed limit - THIS IS NOT AN ERROR FOR US
        if (response.status === 400 && responseData.error === 'VOLUME_EXCEEDS_LIMIT') {
          console.log('Volume exceeds limit - returning special response');
          return {
            volumeCubicMeters: responseData.volume || null,
            totalCost: null,
            totalCostIncludingBasePrice: null,
            shippingType: data.shippingType,
            error: 'VOLUME_EXCEEDS_LIMIT',
            errorMessage: responseData.message || 'Volume exceeds maximum allowed limit',
            statusCode: 400
          };
        }
        
        // For other errors, throw
        const error = new Error(responseData.message || 'Failed to calculate shipping price');
        error.response = { status: response.status, data: responseData };
        throw error;
      }

      return responseData;
    } catch (error) {
      console.error('Error calculating shipping price:', error);
      throw error;
    }
  };

  const recalculateShippingPrice = async () => {
    if (!formData.port) return;

    const selectedOption = getSelectedOption();
    if (!selectedOption) return;

    const isContainer = formData.shippingType === 'container';
    const containerType = formData.containerType;

    if ((isContainer && !containerType) || (!isContainer && !selectedOption)) {
      return;
    }

    try {
      const shippingData = await calculateShippingPrice({
        carModelId: product?.carModelId || '',
        portId: formData.port?.portId || '',
        shippingOptionType: selectedOption,
        isInquiry: false,
        isContainerShipping: isContainer,
        is20ft: isContainer && containerType === '20ft',
        is40ft: isContainer && containerType === '40ft',
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        mobileNo: formData.phoneNumber,
        emailId: formData.emailId || '',
        userMessage: formData.userMessage || '',
        guestCountryCode: formData.countryCode,
        shippingType: getShippingTypeForPayload()
      });

      setFormData(prev => ({
        ...prev,
        shippingCalculation: {
          volumeCubicMeters: shippingData.volumeCubicMeters,
          totalCost: shippingData.totalCost,
          totalCostIncludingBasePrice: shippingData.totalCostIncludingBasePrice,
          containerRate: shippingData.containerRate,
          containerType: shippingData.containerType,
          isContainerShipping: shippingData.isContainerShipping,
          shippingType: shippingData.shippingType,
          error: shippingData.error || null,
          errorMessage: shippingData.errorMessage || null
        }
      }));
    } catch (error) {
      console.error('Error calculating shipping:', error);
      
      // Check if it's a volume exceed limit error from the catch block
      if (error.response?.status === 400 && error.response?.data?.error === 'VOLUME_EXCEEDS_LIMIT') {
        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            volumeCubicMeters: error.response.data.volume || null,
            totalCost: null,
            totalCostIncludingBasePrice: null,
            error: 'VOLUME_EXCEEDS_LIMIT',
            errorMessage: error.response.data.message || 'Volume exceeds maximum allowed limit',
            statusCode: 400
          }
        }));
      } else if (error.response?.status === 401) {
        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            ...prev.shippingCalculation,
            totalCost: null,
            totalCostIncludingBasePrice: null,
            error: 'UNAUTHORIZED'
          }
        }));
      } else if (error.response?.status === 404 || error.message?.includes('Container rate not found') ||
        error.message?.includes('not found') || error.message?.includes('No rates')) {
        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            ...prev.shippingCalculation,
            totalCost: null,
            totalCostIncludingBasePrice: null,
            error: 'CONTAINER_RATE_NOT_FOUND'
          }
        }));
      }
    }
  };

  // Validation function - must be exactly  AND have country code
  const validateForm = () => {
    if (!formData.countryCode) {
      setErrorMessage('Please select a country code');
      setShowErrorPopup(true);
      return false;
    }

    if (!formData.phoneNumber || formData.phoneNumber.length < 4 || formData.phoneNumber.length > 15) {
      setErrorMessage('Please enter a valid phone number');
      setShowErrorPopup(true);
      return false;
    }
    return true;
  };

  const handleInputChange = (field) => (e) => {
    let value = e.target.value;

    if (field === 'phoneNumber') {
      value = value.replace(/\D/g, '');
      value = value.slice(0, 15);
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCountryCodeChange = (code, item) => {
    setFormData(prev => ({
      ...prev,
      countryCode: code,
      countryCodeId: item?.countryId || null,
      guestCountryCode: code,
      phoneNumber: ''
    }));
  };

  const handleCountryChange = (country) => {
    setFormData(prev => ({
      ...prev,
      country: country,
      port: null,
      shippingOption: null,
      shippingCalculation: null,
      shippingType: null,
      containerType: null,
      containerShippingOption: null
    }));
    setPortsList([]);

    // Auto-select country code based on selected country for ALL users
    if (country?.countryName && COUNTRY_NAME_TO_CODE_MAP[country.countryName]) {
      const countryCode = COUNTRY_NAME_TO_CODE_MAP[country.countryName];
      // Phone list me wahi country dhundo (pehle id, phir naam se) - taaki US/Canada jaisa mix-up na ho
      const phoneItem =
        countryCodes.find(c => c.countryId === country.countryId) ||
        countryCodes.find(c => c.country === country.countryName);
      // Profile wali hi country chuni to profile ka phone wapas dikhao, warna phone reset
      const isProfileCountry = isUserLoggedIn && !!userDetails && (
        userDetails.countryId
          ? userDetails.countryId === (phoneItem?.countryId ?? country.countryId)
          : (userDetails.countryCode || '').replace(/\D/g, '') === (phoneItem ? phoneItem.code : countryCode).replace(/\D/g, '')
      );
      const profilePhone = (userDetails?.userMobileNumber || '').replace(/\D/g, '').slice(0, 15);
      setFormData(prev => ({
        ...prev,
        countryCode: phoneItem ? phoneItem.code : countryCode,
        countryCodeId: phoneItem?.countryId || null,
        guestCountryCode: phoneItem ? phoneItem.code : countryCode,
        phoneNumber: isProfileCountry ? profilePhone : ''
      }));
    }

    if (country) {
      fetchPorts(country.countryId);
    }
  };

  const handlePortChange = (port) => {
    setFormData(prev => ({
      ...prev,
      port: port,
      shippingOption: null,
      shippingCalculation: null,
      shippingType: null,
      containerType: null,
      containerShippingOption: null
    }));
  };

  const handleShippingTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      shippingType: type,
      shippingOption: null,
      containerType: null,
      containerShippingOption: null,
      shippingCalculation: null
    }));
  };

  const handleContainerSelection = (containerType) => {
    setFormData(prev => ({
      ...prev,
      containerType,
      containerShippingOption: null,
      shippingCalculation: null
    }));
  };

  const handleIndividualShippingOptionChange = async (newShippingOption) => {
    setFormData(prev => ({
      ...prev,
      shippingOption: newShippingOption
    }));

    if (formData.port && newShippingOption) {
      try {
        const shippingData = await calculateShippingPrice({
          carModelId: product?.carModelId || '',
          portId: formData.port?.portId || '',
          shippingOptionType: newShippingOption,
          isInquiry: false,
          isContainerShipping: false,
          is20ft: false,
          is40ft: false,
          firstName: formData.firstName || '',
          lastName: formData.lastName || '',
          mobileNo: formData.phoneNumber,
          emailId: formData.emailId || '',
          userMessage: formData.userMessage || '',
          guestCountryCode: formData.countryCode,
          shippingType: 'individual shippment'
        });

        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            volumeCubicMeters: shippingData.volumeCubicMeters,
            totalCost: shippingData.totalCost,
            totalCostIncludingBasePrice: shippingData.totalCostIncludingBasePrice,
            shippingType: shippingData.shippingType,
            error: shippingData.error || null,
            errorMessage: shippingData.errorMessage || null
          }
        }));
      } catch (error) {
        console.error('Error calculating shipping:', error);
        if (error.response?.status === 400 && error.response?.data?.error === 'VOLUME_EXCEEDS_LIMIT') {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              volumeCubicMeters: error.response.data.volume || null,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'VOLUME_EXCEEDS_LIMIT',
              errorMessage: error.response.data.message || 'Volume exceeds maximum allowed limit',
              statusCode: 400
            }
          }));
        } else if (error.response?.status === 401) {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              ...prev.shippingCalculation,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'UNAUTHORIZED'
            }
          }));
        }
      }
    }
  };

  const handleContainerShippingOptionSelection = async (option) => {
    setFormData(prev => ({ ...prev, containerShippingOption: option }));

    if (formData.port && formData.containerType && option) {
      try {
        const shippingData = await calculateShippingPrice({
          carModelId: product?.carModelId || '',
          portId: formData.port?.portId || '',
          shippingOptionType: option,
          isInquiry: false,
          isContainerShipping: true,
          is20ft: formData.containerType === '20ft',
          is40ft: formData.containerType === '40ft',
          firstName: formData.firstName || '',
          lastName: formData.lastName || '',
          mobileNo: formData.phoneNumber,
          emailId: formData.emailId || '',
          userMessage: formData.userMessage || '',
          guestCountryCode: formData.countryCode,
          shippingType: getShippingTypeForPayload()
        });

        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            volumeCubicMeters: shippingData.volumeCubicMeters,
            totalCost: shippingData.totalCost,
            totalCostIncludingBasePrice: shippingData.totalCostIncludingBasePrice,
            containerRate: shippingData.containerRate,
            containerType: shippingData.containerType,
            isContainerShipping: shippingData.isContainerShipping,
            shippingType: shippingData.shippingType,
            error: shippingData.error || null,
            errorMessage: shippingData.errorMessage || null
          }
        }));
      } catch (error) {
        console.error('Error calculating container shipping:', error);
        if (error.response?.status === 400 && error.response?.data?.error === 'VOLUME_EXCEEDS_LIMIT') {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              volumeCubicMeters: error.response.data.volume || null,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'VOLUME_EXCEEDS_LIMIT',
              errorMessage: error.response.data.message || 'Volume exceeds maximum allowed limit',
              statusCode: 400
            }
          }));
        } else if (error.response?.status === 401) {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              ...prev.shippingCalculation,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'UNAUTHORIZED'
            }
          }));
        } else if (error.response?.status === 404 || error.message?.includes('Container rate not found')) {
          setFormData(prev => ({
            ...prev,
            shippingCalculation: {
              ...prev.shippingCalculation,
              totalCost: null,
              totalCostIncludingBasePrice: null,
              error: 'CONTAINER_RATE_NOT_FOUND'
            }
          }));
        }
      }
    }
  };

  const getSelectedOption = () => {
    return formData.shippingType === 'container' ? formData.containerShippingOption : formData.shippingOption;
  };

  const shouldShowAsk = () => {
  // Show "Quote on Request" for volume exceed limit in all cases
  if (formData.shippingCalculation?.error === 'VOLUME_EXCEEDS_LIMIT') {
    return true;
  }
  
  if (formData.shippingType === 'container') {
    return formData.shippingCalculation?.error === 'CONTAINER_RATE_NOT_FOUND' ||
      formData.shippingCalculation?.error === 'UNAUTHORIZED' ||
      !formData.shippingCalculation?.totalCostIncludingBasePrice;
  } else {
    const hasDimensionIssue = (formData.shippingOption === "CIF" || formData.shippingOption === "CNF") &&
      (!product?.carLength || !product?.carWidth || !product?.carHeight);
    const hasUnauthorizedError = formData.shippingCalculation?.error === 'UNAUTHORIZED';
    return hasDimensionIssue || hasUnauthorizedError;
  }
};

  const shouldShowShippingCostAsk = () => {
    if (formData.shippingCalculation?.error === 'VOLUME_EXCEEDS_LIMIT') {
      return true;
    }
    
    if (formData.shippingType === 'container') {
      return formData.shippingCalculation?.error === 'CONTAINER_RATE_NOT_FOUND' ||
        formData.shippingCalculation?.error === 'UNAUTHORIZED';
    } else {
      const hasDimensionIssue = (formData.shippingOption === "CIF" || formData.shippingOption === "CNF") &&
        (!product?.carLength || !product?.carWidth || !product?.carHeight);
      const hasUnauthorizedError = formData.shippingCalculation?.error === 'UNAUTHORIZED';
      return hasDimensionIssue || hasUnauthorizedError;
    }
  };

  const resetFormData = () => {
    const resetData = {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      emailId: '',
      userMessage: '',
      country: null,
      port: null,
      shippingOption: null,
      shippingCalculation: null,
      shippingType: null,
      containerType: null,
      containerShippingOption: null,
      countryCode: isUserLoggedIn ? formData.countryCode : '',
      countryCodeId: isUserLoggedIn ? formData.countryCodeId : null,
      guestCountryCode: isUserLoggedIn ? formData.countryCode : ''
    };

    if (isUserLoggedIn && userDetails) {
      resetData.firstName = userDetails.firstName || '';
      resetData.lastName = userDetails.lastName || '';
      resetData.emailId = userDetails.email || '';
    }

    setFormData(resetData);
    setCountriesList([]);
    setPortsList([]);
    setInquiryType('general');
    setErrorMessage('');
  };

  const handleModalClose = () => {
    resetFormData();
    onClose();
  };

  const handleSubmitInquiry = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch('https://car.amar.co.jp/api/dashboard/inquiry/send-inquiry', {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Accept-Language': 'en',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          emailId: formData.emailId,
          phoneNumber: formData.phoneNumber,
          description: formData.userMessage,
          modelId: product?.carModelId,
          guestCountryCode: formData.countryCode
        })
      });

      const data = await response.json();

      if (response.ok) {
        setShowSuccessPopup(true);

        const resetData = {
          firstName: '',
          lastName: '',
          emailId: '',
          phoneNumber: '',
          userMessage: '',
          country: null,
          port: null,
          shippingOption: null,
          shippingCalculation: null,
          shippingType: null,
          containerType: null,
          containerShippingOption: null,
          countryCode: isUserLoggedIn ? formData.countryCode : '',
          guestCountryCode: isUserLoggedIn ? formData.countryCode : ''
        };

        if (isUserLoggedIn && userDetails) {
          resetData.firstName = userDetails.firstName || '';
          resetData.lastName = userDetails.lastName || '';
          resetData.emailId = userDetails.email || '';
        }

        setFormData(resetData);
      } else {
        setErrorMessage('Failed to submit inquiry. Please try again.');
        setShowErrorPopup(true);
      }
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      setErrorMessage('Failed to submit inquiry. Please try again.');
      setShowErrorPopup(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitShippingInquiry = async () => {
    if (!validateForm()) {
      return;
    }

    if (!formData.port || (!formData.shippingOption && !formData.containerShippingOption)) {
      setErrorMessage('Please complete all required fields');
      setShowErrorPopup(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const isContainer = formData.shippingType === 'container';

      const shippingData = {
        carModelId: product?.carModelId,
        portId: formData.port.portId,
        shippingOptionType: isContainer ? formData.containerShippingOption : formData.shippingOption,
        isInquiry: true,
        isContainerShipping: isContainer,
        is20ft: isContainer && formData.containerType === '20ft',
        is40ft: isContainer && formData.containerType === '40ft',
        firstName: formData.firstName,
        lastName: formData.lastName,
        mobileNo: formData.phoneNumber,
        emailId: formData.emailId,
        userMessage: formData.userMessage,
        guestCountryCode: formData.countryCode,
        shippingType: getShippingTypeForPayload()
      };

      const response = await calculateShippingPrice(shippingData);
      
      // Check if it's a volume exceed limit response (still a successful inquiry submission)
      if (response.error === 'VOLUME_EXCEEDS_LIMIT') {
        console.log('Volume exceeds limit - inquiry submitted successfully with special request');
        
        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            volumeCubicMeters: response.volume || null,
            totalCost: null,
            totalCostIncludingBasePrice: null,
            error: 'VOLUME_EXCEEDS_LIMIT',
            errorMessage: response.message || 'Volume exceeds maximum allowed limit',
            statusCode: 400
          }
        }));
        
        setShowSuccessPopup(true);
        
        setTimeout(() => {
          handleModalClose();
        }, 2000);
        
        return;
      }

      // Successful response with calculation
      setFormData(prev => ({
        ...prev,
        shippingCalculation: {
          ...prev.shippingCalculation,
          ...response,
          shippingType: response.shippingType
        }
      }));
      
      setShowSuccessPopup(true);
      
      setTimeout(() => {
        handleModalClose();
      }, 2000);

    } catch (error) {
      console.error('Error submitting shipping inquiry:', error);
      
      // Double-check if this is a volume exceed error that wasn't caught above
      if (error.response?.status === 400 && error.response?.data?.error === 'VOLUME_EXCEEDS_LIMIT') {
        console.log('Volume exceeds limit caught in catch - inquiry was still submitted');
        
        setFormData(prev => ({
          ...prev,
          shippingCalculation: {
            volumeCubicMeters: error.response.data.volume || null,
            totalCost: null,
            totalCostIncludingBasePrice: null,
            error: 'VOLUME_EXCEEDS_LIMIT',
            errorMessage: error.response.data.message || 'Volume exceeds maximum allowed limit',
            statusCode: 400
          }
        }));
        
        setShowSuccessPopup(true);
        
        setTimeout(() => {
          handleModalClose();
        }, 2000);
      } else {
        setErrorMessage('Failed to submit shipping inquiry. Please try again.');
        setShowErrorPopup(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Render phone number field with country code dropdown - smaller width for country code, larger for phone
  const renderPhoneNumberField = () => {
    const selectedCountryName = formData.country?.countryName || '';
    const hasPhoneError = formData.phoneNumber && formData.phoneNumber.length !== 15;

    return (
      <Grid item xs={12} md={6}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CountryCodeSelect
                value={formData.countryCode || ''}
                selectedId={formData.countryCodeId}
                onChange={handleCountryCodeChange}
                countryName={selectedCountryName}
                isLoggedIn={isUserLoggedIn}
                options={countryCodes}
              />

              {!formData.countryCode && (
                <FormHelperText sx={{ color: '#f44336', m: 0, mt: 0.5, fontSize: '.875rem' }}>
                  Required
                </FormHelperText>
              )}
            </Box>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TextField
                fullWidth
                size="small"
                id="phoneNumber"
                placeholder="Enter your Phone Number"
                label="Phone Number"
                value={formData.phoneNumber || ""}
                onChange={handleInputChange('phoneNumber')}
                required
                variant="outlined"
                error={hasPhoneError}
                // helperText={hasPhoneError ? 'Must be exactly 10 digits' : ''}
                FormHelperTextProps={{
                  sx: { color: '#f44336', fontSize: '0.7rem', ml: 0 }
                }}
                inputProps={{
                  maxLength: 15,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: '6px',
                    "&.Mui-focused fieldset": {
                      borderColor: hasPhoneError ? "#f44336" : "#ff3b30",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: hasPhoneError ? "#f44336" : "rgba(0, 0, 0, 0.6)",
                    fontSize: '0.875rem',
                    "&.Mui-focused": {
                      color: hasPhoneError ? "#f44336" : "#ff3b30",
                    },
                  },
                }}
              />
            </Box>
          </Box>

          {isUserLoggedIn && userDetails && (
            <Typography variant="caption" color="#ff3b30" sx={{ mt: 0.5, fontStyle: 'italic', fontSize: '0.7rem' }}>
              Using your registered information. You can update the country code if needed.
            </Typography>
          )}

          {!isUserLoggedIn && selectedCountryName && (
            <Typography variant="caption" color="#d05015ff" sx={{ mt: 0.5, fontStyle: 'italic', fontSize: '0.7rem' }}>
              Suggested: {selectedCountryName}
            </Typography>
          )}
        </Box>
      </Grid>
    );
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleModalClose}
        aria-labelledby="inquiry-modal-title"
        aria-describedby="inquiry-modal-description"
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100vh",
          }}
          onClick={handleModalClose}
        >
          <Container
            maxWidth="lg"
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "white",
              borderRadius: "8px",
              boxShadow: 24,
              width: "90%",
              maxHeight: "90vh",
              overflow: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Box sx={{ maxHeight: "80vh", overflowY: "auto" }}>
              {/* Header */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  width: "100%",
                  position: "sticky",
                  borderBottom: "1px solid #cccccc",
                  top: 0,
                  backgroundColor: "#fff",
                  zIndex: 10,
                  padding: "10px",
                }}
              >
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  sx={{ margin: 0, fontSize: "24px" }}
                >
                  Get a Free Quotation
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 3 }}>
                  <Box sx={{ textAlign: "right", display: "flex", alignItems: "center", gap: "10px" }}>
                    <Typography
                      variant="body2"
                      color="#180202ff"
                      fontWeight="600"
                      sx={{ fontSize: "20px" }}
                    >
                      {getHeaderTitle()}
                    </Typography>
                    <Typography
                      variant="h6"
                      color="#ff3b30"
                      fontWeight="700"
                      sx={{ fontSize: "22px" }}
                    >
                      {shouldShowAsk() ? 'Quote on Request' : (
                        formData.shippingCalculation?.totalCostIncludingBasePrice
                          ? `$${formData.shippingCalculation.totalCostIncludingBasePrice.toFixed(2)}`
                          : `$${product?.carPrice?.toFixed(2) || "0.00"}`
                      )}
                    </Typography>
                  </Box>

                  <IconButton onClick={handleModalClose}>
                    <CloseIcon />
                  </IconButton>
                </Box>
              </Box>

              {/* Product Info */}
              <Grid container spacing={4} sx={{ marginTop: "10px" }}>
                <Grid item xs={12} md={6}>
                  <img
                    src={imageUrl1}
                    alt={`${product?.brandName} ${product?.modelName}`}
                    style={{ width: "100%", borderRadius: "8px" }}
                    onError={(e) => {
                      if (!imageError) {
                        setImageError(true);
                        e.target.src = "carousel/carslider.png";
                      }
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h4" fontWeight="bold" mt={2}>
                    {`${product?.brandName} ${product?.modelName} ${product?.categoriesName}`}
                  </Typography>

                  <Typography variant="subtitle1" color="text.secondary" sx={{ marginTop: '12px' }}>
                    {`${product?.carMileage} | ${product?.carTransmission} | ${product?.carDrive} | ${product?.carEngineCC}`}
                  </Typography>
                  <Typography variant="body1" sx={{ marginTop: '24px' }}>
                    {product?.categoriesName} with {product?.carColor} exterior
                  </Typography>

                  <Typography variant="h4" color="#ff3b30" fontWeight="bold" sx={{ marginTop: '30px' }}>
                    {`$${product?.carPrice?.toFixed(2) || "0.00"}`}
                  </Typography>
                </Grid>
              </Grid>

              {/* Specifications Toggle */}
              <Box sx={{ display: "flex", justifyContent: "center", mb: 2, marginTop: "30px" }}>
                <Button
                  variant="contained"
                  onClick={() => setOpenSpecifications((prevState) => !prevState)}
                  sx={{
                    bgcolor: "#ff3b30",
                    "&:hover": {
                      bgcolor: "#e03228",
                    },
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {openSpecifications ? (
                    <>
                      View Less Specifications <ExpandLessIcon />
                    </>
                  ) : (
                    <>
                      View More Specifications <ExpandMoreIcon />
                    </>
                  )}
                </Button>
              </Box>

              {openSpecifications && (
                <TableContainer component={Paper}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Brand
                        </TableCell>
                        <TableCell>{product?.brandName || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Drive Type
                        </TableCell>
                        <TableCell>{product?.carDrive || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Transmission
                        </TableCell>
                        <TableCell>{product?.carTransmission || '-'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Year
                        </TableCell>
                        <TableCell>{product?.year || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Model
                        </TableCell>
                        <TableCell>{product?.modelName || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Category
                        </TableCell>
                        <TableCell>{product?.categoriesName || '-'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Reference Number
                        </TableCell>
                        <TableCell>{product?.carRefNumber || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Car Dimensions (m)
                        </TableCell>
                        <TableCell>
                          {product?.carLength || '-'} x {product?.carWidth || '-'} x {product?.carHeight || ''}
                        </TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Engine CC
                        </TableCell>
                        <TableCell>{product?.carEngineCC || '-'} cc</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Mileage
                        </TableCell>
                        <TableCell>{product?.carMileage || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Fuel Type
                        </TableCell>
                        <TableCell>{product?.carFuel || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Steering
                        </TableCell>
                        <TableCell>{product?.carSteering || '-'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Color
                        </TableCell>
                        <TableCell>{product?.carColor || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Seats
                        </TableCell>
                        <TableCell>{product?.carSeat || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Doors
                        </TableCell>
                        <TableCell>{product?.carDoor || '-'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Chassis Number
                        </TableCell>
                        <TableCell>{product?.carChassisNumber || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Recycle Fee Paid
                        </TableCell>
                        <TableCell>{product?.isRecycleAmountPaidText || '--'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Model Code
                        </TableCell>
                        <TableCell>{product?.carModelCode || '-'}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Location
                        </TableCell>
                        <TableCell>{product?.carLocation || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Capacity
                        </TableCell>
                        <TableCell>{product?.carSeat || '-'}</TableCell>
                        <TableCell sx={{ bgcolor: "#f5f5f5", fontWeight: "medium" }}>
                          Import Type
                        </TableCell>
                        <TableCell>{product?.importType || '-'}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
                <Typography
                  component="span"
                  variant="h8"
                  fontWeight="600"
                  sx={{
                    backgroundColor: "lightyellow",
                    padding: "4px 8px",
                    borderRadius: "4px",
                    display: "inline-block"
                  }}
                >
                  Make Sure All * Required Fields Are Properly Filled.
                </Typography>
              </Box>

              {/* Main Form Card */}
              <Box sx={{ marginBottom: "20px" }}>
                <Card sx={{
                  background: 'white',
                  border: '2px solid #ff3b30',
                  borderRadius: '16px',
                  padding: { xs: '16px', md: '24px' },
                  maxWidth: '100%',
                  margin: 'auto',
                  position: 'relative',
                  overflow: 'visible',
                  boxShadow: '0 8px 16px rgba(255, 59, 48, 0.2)',
                  '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 59, 48, 0.2)',
                    zIndex: -1
                  }
                }}>
                  <CardContent>
                    <Typography variant="h5" sx={{
                      color: '#ff3b30',
                      mb: 3,
                      textAlign: 'center',
                      textTransform: 'uppercase',
                      fontWeight: 'bold',
                      letterSpacing: '1px',
                      position: 'relative',
                      '&:after': {
                        content: '""',
                        position: 'absolute',
                        bottom: -8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '60px',
                        height: '2px',
                        backgroundColor: '#ff3b30'
                      }
                    }}>
                      Vehicle Inquiry Form
                    </Typography>

                    {/* Inquiry Type Selection */}
                    <Box sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      mb: 3,
                      gap: 1,
                      flexWrap: 'wrap'
                    }}>
                      <Button
                        variant={inquiryType === 'general' ? 'contained' : 'outlined'}
                        onClick={() => setInquiryType('general')}
                        sx={{
                          backgroundColor: inquiryType === 'general' ? '#ff3b30' : 'transparent',
                          color: inquiryType === 'general' ? 'white' : '#ff3b30',
                          border: '1.5px solid #ff3b30',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          transition: 'all 0.2s',
                          minWidth: '120px',
                          '&:hover': {
                            backgroundColor: inquiryType === 'general' ? '#ff5047' : 'rgba(255, 59, 48, 0.1)',
                            borderColor: '#ff3b30'
                          }
                        }}
                      >
                        General Inquiry
                      </Button>
                      <Button
                        variant={inquiryType === 'shipping' ? 'contained' : 'outlined'}
                        onClick={() => setInquiryType('shipping')}
                        sx={{
                          backgroundColor: inquiryType === 'shipping' ? '#ff3b30' : 'transparent',
                          color: inquiryType === 'shipping' ? 'white' : '#ff3b30',
                          border: '1.5px solid #ff3b30',
                          borderRadius: '6px',
                          padding: '8px 16px',
                          fontWeight: 'bold',
                          fontSize: '0.875rem',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          transition: 'all 0.2s',
                          minWidth: '160px',
                          '&:hover': {
                            backgroundColor: inquiryType === 'shipping' ? '#ff5047' : 'rgba(255, 59, 48, 0.1)',
                            borderColor: '#ff3b30'
                          }
                        }}
                      >
                        Shipping Quote
                      </Button>
                    </Box>

                    {/* General Inquiry Form */}
                    {inquiryType === 'general' && (
                      <Box sx={{
                        backgroundColor: 'rgba(255, 59, 48, 0.05)',
                        borderRadius: '10px',
                        padding: '16px',
                        border: '1px dashed rgba(255, 59, 48, 0.2)'
                      }}>
                        <Typography variant="h6" sx={{
                          color: '#ff3b30',
                          mb: 2,
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontSize: '1rem'
                        }}>
                          <MailOutlineIcon fontSize="small" /> General Inquiry Form
                        </Typography>

                        <Grid container spacing={2}>
                          {['firstName', 'lastName'].map((field) => (
                            <Grid item xs={12} md={6} key={field}>
                              <TextField
                                fullWidth
                                size="small"
                                id={field}
                                label={labelMap[field]}
                                value={formData[field] || ""}
                                onChange={handleInputChange(field)}
                                required
                                variant="outlined"
                                InputLabelProps={{ shrink: true }}
                                sx={{
                                  '& .MuiOutlinedInput-root': {
                                    borderRadius: '6px',
                                    '& fieldset': {
                                      borderColor: '#cccccc',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: '#ff3b30',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#ff3b30',
                                      borderWidth: '2px',
                                    },
                                  },
                                  '& .MuiInputLabel-root': {
                                    color: '#666',
                                    '&.Mui-focused': { color: '#ff3b30' },
                                    fontSize: '0.875rem',
                                    padding: '0 4px',
                                  },
                                }}
                              />
                            </Grid>
                          ))}

                          {renderPhoneNumberField()}

                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              size="small"
                              id="emailId"
                              label="Email Address"
                              value={formData.emailId || ""}
                              onChange={handleInputChange('emailId')}
                              required
                              variant="outlined"
                              InputLabelProps={{ shrink: true }}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '6px',
                                  '& fieldset': {
                                    borderColor: '#cccccc',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#ff3b30',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#ff3b30',
                                    borderWidth: '2px',
                                  },
                                },
                                '& .MuiInputLabel-root': {
                                  color: '#666',
                                  '&.Mui-focused': { color: '#ff3b30' },
                                  fontSize: '0.875rem',
                                  padding: '0 4px',
                                },
                              }}
                            />
                          </Grid>

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              minRows={3}
                              variant="outlined"
                              id="userMessage"
                              size="small"
                              label="Your Message"
                              value={formData.userMessage}
                              InputLabelProps={{
                                shrink: true,
                                style: { fontSize: '0.875rem' }
                              }}
                              onChange={handleInputChange('userMessage')}
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '6px',
                                  // border: "1px solid #cccccc",
                                  '& fieldset': { borderColor: '#ddd' },
                                  '&:hover fieldset': { borderColor: '#ff3b30' },
                                  '&.Mui-focused fieldset': { borderColor: '#ff3b30' }
                                },
                                '& .MuiInputBase-input': {
                                  padding: '8.5px 14px',
                                  fontSize: '0.875rem',
                                  lineHeight: '1.5',
                                  minHeight: 'unset'
                                },
                                '& .MuiInputLabel-root': {
                                  color: '#666',
                                  fontSize: '0.875rem',
                                  transform: 'translate(14px, -9px) scale(0.75)',
                                  padding: '0 4px',
                                  // backgroundColor: "white",  <-- REMOVE OR COMMENT OUT THIS LINE
                                  '&.MuiInputLabel-shrink': {
                                    transform: 'translate(14px, -9px) scale(0.75)'
                                  }
                                }
                              }}
                            />
                          </Grid>

                          <Grid item xs={12} sx={{ mt: 1 }}>
                            <Button
                              fullWidth
                              variant="contained"
                              size="medium"
                              onClick={handleSubmitInquiry}
                              disabled={
                                !formData.firstName ||
                                !formData.lastName ||
                                !formData.phoneNumber ||
                                formData.phoneNumber.length < 4 || formData.phoneNumber.length > 15 ||
                                !formData.emailId ||
                                !formData.countryCode ||
                                isSubmitting
                              }
                              sx={{
                                background: 'linear-gradient(45deg, #ff3b30 0%, #ff5e56 100%)',
                                color: 'white',
                                py: 1.5,
                                borderRadius: '6px',
                                fontWeight: 'bold',
                                fontSize: '0.875rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                boxShadow: '0 2px 8px rgba(255, 59, 48, 0.3)',
                                transition: 'all 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 4px 12px rgba(255, 59, 48, 0.4)'
                                },
                                '&:disabled': {
                                  background: '#e0e0e0'
                                }
                              }}
                            >
                              {isSubmitting ? (
                                <>
                                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                  Submitting...
                                </>
                              ) : (
                                'Submit Inquiry'
                              )}
                            </Button>
                          </Grid>
                        </Grid>
                      </Box>
                    )}

                    {/* Shipping Quote Form */}
                    {inquiryType === 'shipping' && (
                      <Box>
                        {/* Step 1: Country Selection */}
                        <Box sx={{
                          mb: 3,
                          p: 2,
                          backgroundColor: formData.country ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                          borderRadius: '10px',
                          border: `1.5px solid ${formData.country ? '#4caf50' : '#ff3b30'}`,
                          transition: 'all 0.2s'
                        }}>
                          <Typography variant="subtitle1" sx={{
                            color: formData.country ? '#4caf50' : '#ff3b30',
                            mb: 1,
                            fontWeight: '600',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            fontSize: '0.875rem'
                          }}>
                            {formData.country ? <CheckCircleOutlineIcon fontSize="small" /> : <LocationOnIcon fontSize="small" />}
                            {formData.country ? 'Country Selected' : 'Step 1: Select Destination'}
                          </Typography>

                          <SearchableSelect
                            value={formData.country}
                            options={countriesList}
                            onChange={handleCountryChange}
                            onOpen={() => {
                              if (countriesList.length === 0) fetchCountries();
                            }}
                            placeholder="Click to search countries..."
                            searchPlaceholder="Type to search countries..."
                            getOptionLabel={(option) => option?.countryName || ''}
                            getOptionValue={(option) => option?.countryId || ''}
                          />
                        </Box>

                        {/* Step 2: Port Selection */}
                        {formData.country && (
                          <Box sx={{
                            mb: 3,
                            p: 2,
                            backgroundColor: formData.port ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                            borderRadius: '10px',
                            border: `1.5px solid ${formData.port ? '#4caf50' : '#ff3b30'}`,
                            transition: 'all 0.2s'
                          }}>
                            <Typography variant="subtitle1" sx={{
                              color: formData.port ? '#4caf50' : '#ff3b30',
                              mb: 1,
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontSize: '0.875rem'
                            }}>
                              {formData.port ? <CheckCircleOutlineIcon fontSize="small" /> : <DirectionsBoatIcon fontSize="small" />}
                              {formData.port ? 'Port Selected' : 'Step 2: Select Port'}
                            </Typography>

                            <SearchableSelect
                              value={formData.port}
                              options={portsList}
                              onChange={handlePortChange}
                              placeholder="Click to search ports..."
                              searchPlaceholder="Type to search ports..."
                              disabled={!formData.country}
                              getOptionLabel={(option) => option?.portName || ''}
                              getOptionValue={(option) => option?.portId || ''}
                            />
                          </Box>
                        )}

                        {/* Step 3: Shipping Type Selection */}
                        {formData.port && (
                          <Box sx={{
                            mb: 3,
                            p: 2,
                            backgroundColor: formData.shippingType ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                            borderRadius: '10px',
                            border: `1.5px solid ${formData.shippingType ? '#4caf50' : '#ff3b30'}`,
                            transition: 'all 0.2s'
                          }}>
                            <Typography variant="subtitle1" sx={{
                              color: formData.shippingType ? '#4caf50' : '#ff3b30',
                              mb: 1,
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontSize: '0.875rem'
                            }}>
                              {formData.shippingType ? <CheckCircleOutlineIcon fontSize="small" /> : <LocalShippingIcon fontSize="small" />}
                              {formData.shippingType ? 'Shipping Type Selected' : 'Step 3: Select Shipping Type'}
                            </Typography>

                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Box
                                  onClick={() => handleShippingTypeChange('individual')}
                                  sx={{
                                    p: 2,
                                    border: formData.shippingType === 'individual' ? '2px solid #ff3b30' : '2px solid #ddd',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    backgroundColor: formData.shippingType === 'individual' ? 'rgba(255, 59, 48, 0.05)' : 'white',
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                      borderColor: '#ff3b30',
                                      backgroundColor: 'rgba(255, 59, 48, 0.05)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 12px rgba(255, 59, 48, 0.2)'
                                    }
                                  }}
                                >
                                  <LocalShippingIcon sx={{ fontSize: 40, mb: 1, color: formData.shippingType === 'individual' ? '#ff3b30' : '#ff3b30' }} />
                                  <Typography variant="h6" fontWeight="700" fontSize="1rem">
                                    Individual Shipment(RORO)
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                    FOB, C&F, CIF Options
                                  </Typography>
                                </Box>
                              </Grid>

                              <Grid item xs={12} md={6}>
                                <Box
                                  onClick={() => handleShippingTypeChange('container')}
                                  sx={{
                                    p: 2,
                                    border: formData.shippingType === 'container' ? '2px solid #ff3b30' : '2px solid #ddd',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    backgroundColor: formData.shippingType === 'container' ? 'rgba(255, 59, 48, 0.05)' : 'white',
                                    transition: 'all 0.3s',
                                    '&:hover': {
                                      borderColor: '#ff3b30',
                                      backgroundColor: 'rgba(255, 59, 48, 0.05)',
                                      transform: 'translateY(-2px)',
                                      boxShadow: '0 4px 12px rgba(255, 59, 48, 0.2)'
                                    }
                                  }}
                                >
                                  <InventoryIcon sx={{ fontSize: 40, mb: 1, color: formData.shippingType === 'container' ? '#ff3b30' : '#ff3b30' }} />
                                  <Typography variant="h6" fontWeight="700" fontSize="1rem">
                                    Container Shipment
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" fontSize="0.75rem">
                                    20ft & 40ft Containers
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>
                          </Box>
                        )}

                        {/* Step 4a: Individual Shipping Options (CIF, C&F, FOB) */}
                        {formData.shippingType === 'individual' && (
                          <Box sx={{
                            mb: 3,
                            p: 2,
                            backgroundColor: formData.shippingOption ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                            borderRadius: '10px',
                            border: `1.5px solid ${formData.shippingOption ? '#4caf50' : '#ff3b30'}`,
                            transition: 'all 0.2s'
                          }}>
                            <Typography variant="subtitle1" sx={{
                              color: formData.shippingOption ? '#4caf50' : '#ff3b30',
                              mb: 1,
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontSize: '0.875rem'
                            }}>
                              {formData.shippingOption ? <CheckCircleOutlineIcon fontSize="small" /> : <LocalShippingIcon fontSize="small" />}
                              {formData.shippingOption ? 'Shipping Option Selected' : 'Step 4: Select Shipping Option '}
                            </Typography>

                            <RadioGroup
                              value={formData.shippingOption || ''}
                              onChange={(e) => handleIndividualShippingOptionChange(e.target.value)}
                              sx={{ display: 'flex', gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}
                            >
                              {shippingOptions.map((option) => (
                                <FormControlLabel
                                  key={option.value}
                                  value={option.value}
                                  control={
                                    <Radio
                                      size="small"
                                      sx={{
                                        color: '#ff3b30',
                                        '&.Mui-checked': { color: '#ff3b30' },
                                        padding: '6px'
                                      }}
                                    />
                                  }
                                  label={
                                    <Box sx={{
                                      border: formData.shippingOption === option.value ?
                                        '1.5px solid #ff3b30' : '1px solid #ddd',
                                      borderRadius: '6px',
                                      p: 1,
                                      backgroundColor: formData.shippingOption === option.value ?
                                        'rgba(255, 59, 48, 0.05)' : 'white',
                                      flex: 1,
                                      transition: 'all 0.2s'
                                    }}>
                                      <Typography fontWeight="600" fontSize="0.875rem">
                                        {option.label.split(' (')[0]}
                                      </Typography>
                                      <Typography fontSize="0.75rem" color="text.secondary">
                                        {option.label.includes('(') ? option.label.split(' (')[1].replace(')', '') : ''}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{
                                    m: 0,
                                    flex: 1,
                                    minWidth: '150px',
                                    '& .MuiFormControlLabel-label': {
                                      flex: 1,
                                      marginLeft: '4px'
                                    }
                                  }}
                                />
                              ))}
                            </RadioGroup>
                          </Box>
                        )}

                        {/* Step 4b: Container Size Selection */}
                        {formData.shippingType === 'container' && (
                          <Box sx={{
                            mb: 3,
                            p: 2,
                            backgroundColor: formData.containerType ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                            borderRadius: '10px',
                            border: `1.5px solid ${formData.containerType ? '#4caf50' : '#ff3b30'}`,
                            transition: 'all 0.2s'
                          }}>
                            <Typography variant="subtitle1" sx={{
                              color: formData.containerType ? '#4caf50' : '#ff3b30',
                              mb: 1,
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontSize: '0.875rem'
                            }}>
                              {formData.containerType ? <CheckCircleOutlineIcon fontSize="small" /> : <InventoryIcon fontSize="small" />}
                              {formData.containerType ? 'Container Selected' : 'Step 4: Select Container Size'}
                            </Typography>

                            <RadioGroup
                              value={formData.containerType || ''}
                              onChange={(e) => handleContainerSelection(e.target.value)}
                              sx={{ display: 'flex', gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}
                            >
                              <FormControlLabel
                                value="20ft"
                                control={
                                  <Radio
                                    size="small"
                                    sx={{
                                      color: '#ff3b30',
                                      '&.Mui-checked': { color: '#ff3b30' },
                                      padding: '6px'
                                    }}
                                  />
                                }
                                label={
                                  <Box sx={{
                                    border: formData.containerType === '20ft' ?
                                      '1.5px solid #ff3b30' : '1px solid #ddd',
                                    borderRadius: '6px',
                                    p: 1,
                                    backgroundColor: formData.containerType === '20ft' ?
                                      'rgba(255, 59, 48, 0.05)' : 'white',
                                    flex: 1,
                                    transition: 'all 0.2s'
                                  }}>
                                    <Typography fontWeight="600" fontSize="0.875rem">
                                      20ft Container
                                    </Typography>
                                    <Typography fontSize="0.75rem" color="text.secondary">
                                      5.9m × 2.35m × 2.39m
                                    </Typography>
                                  </Box>
                                }
                                sx={{
                                  m: 0,
                                  flex: 1,
                                  minWidth: '150px',
                                  '& .MuiFormControlLabel-label': {
                                    flex: 1,
                                    marginLeft: '4px'
                                  }
                                }}
                              />
                              <FormControlLabel
                                value="40ft"
                                control={
                                  <Radio
                                    size="small"
                                    sx={{
                                      color: '#ff3b30',
                                      '&.Mui-checked': { color: '#ff3b30' },
                                      padding: '6px'
                                    }}
                                  />
                                }
                                label={
                                  <Box sx={{
                                    border: formData.containerType === '40ft' ?
                                      '1.5px solid #ff3b30' : '1px solid #ddd',
                                    borderRadius: '6px',
                                    p: 1,
                                    backgroundColor: formData.containerType === '40ft' ?
                                      'rgba(255, 59, 48, 0.05)' : 'white',
                                    flex: 1,
                                    transition: 'all 0.2s'
                                  }}>
                                    <Typography fontWeight="600" fontSize="0.875rem">
                                      40ft Container
                                    </Typography>
                                    <Typography fontSize="0.75rem" color="text.secondary">
                                      12m × 2.35m × 2.39m
                                    </Typography>
                                  </Box>
                                }
                                sx={{
                                  m: 0,
                                  flex: 1,
                                  minWidth: '150px',
                                  '& .MuiFormControlLabel-label': {
                                    flex: 1,
                                    marginLeft: '4px'
                                  }
                                }}
                              />
                            </RadioGroup>
                          </Box>
                        )}

                        {/* Step 5: Container Shipping Options */}
                        {formData.containerType && (
                          <Box sx={{
                            mb: 3,
                            p: 2,
                            backgroundColor: formData.containerShippingOption ? 'rgba(76, 175, 80, 0.08)' : 'rgba(255, 59, 48, 0.08)',
                            borderRadius: '10px',
                            border: `1.5px solid ${formData.containerShippingOption ? '#4caf50' : '#ff3b30'}`,
                            transition: 'all 0.2s'
                          }}>
                            <Typography variant="subtitle1" sx={{
                              color: formData.containerShippingOption ? '#4caf50' : '#ff3b30',
                              mb: 1,
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontSize: '0.875rem'
                            }}>
                              {formData.containerShippingOption ? <CheckCircleOutlineIcon fontSize="small" /> : <LocalShippingIcon fontSize="small" />}
                              {formData.containerShippingOption ? 'Shipping Option Selected' : 'Step 5: Select Shipping Option'}
                            </Typography>

                            <RadioGroup
                              value={formData.containerShippingOption || ''}
                              onChange={(e) => handleContainerShippingOptionSelection(e.target.value)}
                              sx={{ display: 'flex', gap: 1, flexDirection: 'row', flexWrap: 'wrap' }}
                            >
                              {shippingOptions.map((option) => (
                                <FormControlLabel
                                  key={option.value}
                                  value={option.value}
                                  control={
                                    <Radio
                                      size="small"
                                      sx={{
                                        color: '#ff3b30',
                                        '&.Mui-checked': { color: '#ff3b30' },
                                        padding: '6px'
                                      }}
                                    />
                                  }
                                  label={
                                    <Box sx={{
                                      border: formData.containerShippingOption === option.value ?
                                        '1.5px solid #ff3b30' : '1px solid #ddd',
                                      borderRadius: '6px',
                                      p: 1,
                                      backgroundColor: formData.containerShippingOption === option.value ?
                                        'rgba(255, 59, 48, 0.05)' : 'white',
                                      flex: 1,
                                      transition: 'all 0.2s'
                                    }}>
                                      <Typography fontWeight="600" fontSize="0.875rem">
                                        {option.label.split(' (')[0]}
                                      </Typography>
                                      <Typography fontSize="0.75rem" color="text.secondary">
                                        {option.label.includes('(') ? option.label.split(' (')[1].replace(')', '') : ''}
                                      </Typography>
                                    </Box>
                                  }
                                  sx={{
                                    m: 0,
                                    flex: 1,
                                    minWidth: '150px',
                                    '& .MuiFormControlLabel-label': {
                                      flex: 1,
                                      marginLeft: '4px'
                                    }
                                  }}
                                />
                              ))}
                            </RadioGroup>
                          </Box>
                        )}

                        {/* Cost Summary */}
                        {(formData.shippingOption || formData.containerShippingOption) && (
                          <Box sx={{
                            mb: 3,
                            p: 2,
                            backgroundColor: 'rgba(255, 59, 48, 0.05)',
                            borderRadius: '10px',
                            border: '1.5px solid #ff3b30'
                          }}>
                            <Typography variant="subtitle1" sx={{
                              color: '#ff3b30',
                              mb: 2,
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontSize: '0.875rem'
                            }}>
                              <AttachMoneyIcon fontSize="small" /> COST SUMMARY
                            </Typography>

                            <Grid container spacing={1} sx={{ mb: 2 }}>
                              <Grid item xs={12} md={4}>
                                <Box sx={{
                                  p: 1,
                                  backgroundColor: 'white',
                                  borderRadius: '6px',
                                  border: '1px solid #ddd',
                                  textAlign: 'center',
                                  height: '100%'
                                }}>
                                  <Typography variant="body2" color="#666" fontWeight="500" fontSize="0.75rem">
                                    {formData.shippingType === 'container' ? 'Container Type' : 'Vehicle Price'}
                                  </Typography>
                                  <Typography variant="h6" color="#ff3b30" fontWeight="700" fontSize="1rem">
                                    {formData.shippingType === 'container'
                                      ? (formData.containerType === '20ft' ? '20ft Container' : '40ft Container')
                                      : `$${product?.carPrice?.toFixed(2) || '$0.00'}`}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Box sx={{
                                  p: 1,
                                  backgroundColor: 'white',
                                  borderRadius: '6px',
                                  border: '1px solid #ddd',
                                  textAlign: 'center',
                                  height: '100%'
                                }}>
                                  <Typography variant="h6" color="#666" fontWeight="500" fontSize=".75rem">
                                    Shipping Cost
                                  </Typography>
                                  <Typography variant="h6" color="#ff3b30" fontWeight="700" fontSize="1rem">
                                    {shouldShowShippingCostAsk() ? 'Quote on Request' : (
                                      formData.shippingType === 'container' ? (
                                        formData.shippingCalculation?.totalCost
                                          ? `$${parseFloat(formData.shippingCalculation.totalCost).toFixed(2)}`
                                          : 'Quote on Request'
                                      ) : (
                                        formData.shippingCalculation?.totalCost
                                          ? `$${parseFloat(formData.shippingCalculation.totalCost).toFixed(2)}`
                                          : '$0.00'
                                      )
                                    )}
                                  </Typography>
                                </Box>
                              </Grid>
                              <Grid item xs={12} md={4}>
                                <Box sx={{
                                  p: 1,
                                  backgroundColor: 'white',
                                  borderRadius: '6px',
                                  border: '1px solid #ddd',
                                  textAlign: 'center',
                                  height: '100%'
                                }}>
                                  <Typography variant="body2" color="#666" fontWeight="500" fontSize="0.75rem">
                                    {getCostSummaryTitle()}
                                  </Typography>
                                  <Typography variant="h6" color="#ff3b30" fontWeight="700" fontSize="1rem">
                                    {shouldShowAsk()
                                      ? 'Quote on Request'
                                      : formData.shippingCalculation?.totalCostIncludingBasePrice
                                        ? `$${parseFloat(formData.shippingCalculation.totalCostIncludingBasePrice).toFixed(2)}`
                                        : `$${product?.carPrice?.toFixed(2) || "0.00"}`}
                                  </Typography>
                                </Box>
                              </Grid>
                            </Grid>

                            <Typography variant="caption" color="#faf1f1ff" sx={{ fontStyle: 'normal', fontSize: '12px', fontWeight: 'bold', backgroundColor: 'red', borderRadius: '6px', padding: '8px' }}>
                              {formData.shippingCalculation?.error === 'VOLUME_EXCEEDS_LIMIT' 
                                ? '* Vehicle volume exceeds standard shipping limits. Please contact us for special arrangements.'
                                : formData.shippingType === 'container'
                                  ? '* Container pricing includes shipping to destination port. Contact us for final quote.'
                                  : '* Final shipping costs will be confirmed after vehicle inspection'}
                            </Typography>
                          </Box>
                        )}

                        {/* Personal Information */}
                        {(formData.shippingOption || formData.containerShippingOption) && (
                          <Box sx={{
                            backgroundColor: 'rgba(255, 59, 48, 0.05)',
                            borderRadius: '10px',
                            padding: '16px',
                            border: '1px dashed rgba(255, 59, 48, 0.2)'
                          }}>
                            <Typography variant="subtitle1" sx={{
                              color: '#ff3b30',
                              mb: 2,
                              fontWeight: '600',
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1,
                              fontSize: '0.875rem'
                            }}>
                              <PersonOutlineIcon fontSize="small" /> Contact Information
                            </Typography>

                            <Grid container spacing={2}>
                              {['firstName', 'lastName'].map((field) => (
                                <Grid item xs={12} md={6} key={field}>
                                  <TextField
                                    fullWidth
                                    size="small"
                                    label={labelMap[field]}
                                    value={formData[field]}
                                    onChange={handleInputChange(field)}
                                    required
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: '6px',
                                        '& fieldset': { borderColor: '#ddd' },
                                        '&:hover fieldset': { borderColor: '#ff3b30' },
                                        '&.Mui-focused fieldset': { borderColor: '#ff3b30' }
                                      },
                                      '& .MuiInputLabel-root': {
                                        color: '#666',
                                        '&.Mui-focused': { color: '#ff3b30' },
                                        fontSize: '0.875rem'
                                      }
                                    }}
                                  />
                                </Grid>
                              ))}

                              {renderPhoneNumberField()}

                              <Grid item xs={12} md={6}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  id="emailId"
                                  label="Email Address"
                                  value={formData.emailId || ""}
                                  onChange={handleInputChange('emailId')}
                                  required
                                  variant="outlined"
                                  InputLabelProps={{ shrink: true }}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '6px',
                                      '& fieldset': { borderColor: '#ddd' },
                                      '&:hover fieldset': { borderColor: '#ff3b30' },
                                      '&.Mui-focused fieldset': { borderColor: '#ff3b30' }
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: '#666',
                                      '&.Mui-focused': { color: '#ff3b30' },
                                      fontSize: '0.875rem'
                                    }
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12}>
                                <TextField
                                  fullWidth
                                  multiline
                                  minRows={3}
                                  size="small"
                                  label="Your Message"
                                  value={formData.userMessage}
                                  onChange={handleInputChange('userMessage')}
                                  sx={{
                                    '& .MuiOutlinedInput-root': {
                                      borderRadius: '6px',
                                      '& fieldset': { borderColor: '#ddd' },
                                      '&:hover fieldset': { borderColor: '#ff3b30' }
                                    },
                                    '& .MuiInputLabel-root': {
                                      color: '#666',
                                      fontSize: '0.875rem'
                                    }
                                  }}
                                />
                              </Grid>

                              <Grid item xs={12} sx={{ mt: 1 }}>
                                <Button
                                  fullWidth
                                  variant="contained"
                                  size="medium"
                                  onClick={handleSubmitShippingInquiry}
                                  disabled={
                                    !formData.firstName ||
                                    !formData.lastName ||
                                    !formData.phoneNumber ||
                                    // formData.phoneNumber.length !== 15 ||
                                    formData.phoneNumber.length < 4 || formData.phoneNumber.length > 15 ||
                                    !formData.emailId ||
                                    !formData.countryCode ||
                                    !formData.port ||
                                    (!formData.shippingOption && !formData.containerShippingOption) ||
                                    isSubmitting
                                  }
                                  sx={{
                                    background: 'linear-gradient(45deg, #ff3b30 0%, #ff5e56 100%)',
                                    color: 'white',
                                    py: 1.5,
                                    borderRadius: '6px',
                                    fontWeight: 'bold',
                                    fontSize: '0.875rem',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px',
                                    boxShadow: '0 2px 8px rgba(255, 59, 48, 0.3)',
                                    transition: 'all 0.2s',
                                    '&:hover': {
                                      transform: 'translateY(-1px)',
                                      boxShadow: '0 4px 12px rgba(255, 59, 48, 0.4)'
                                    },
                                    '&:disabled': {
                                      background: '#e0e0e0'
                                    }
                                  }}
                                >
                                  {isSubmitting ? (
                                    <>
                                      <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                                      Processing...
                                    </>
                                  ) : (
                                    'Get Shipping Quote'
                                  )}
                                </Button>
                              </Grid>
                            </Grid>
                          </Box>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Box>
            </Box>
          </Container>
        </Box>
      </Modal>

      {/* Success Popup */}
      <Snackbar
        open={showSuccessPopup}
        autoHideDuration={2000}
        onClose={() => {
          setShowSuccessPopup(false);
          handleModalClose();
        }}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => {
            setShowSuccessPopup(false);
            handleModalClose();
          }}
          severity="success"
          sx={{ width: '100%', backgroundColor: '#4caf50', color: 'white' }}
        >
          Inquiry sent successfully!
        </Alert>
      </Snackbar>

      {/* Error Popup */}
      <Snackbar
        open={showErrorPopup}
        autoHideDuration={6000}
        onClose={() => setShowErrorPopup(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setShowErrorPopup(false)}
          severity="error"
          sx={{ width: '100%', backgroundColor: '#ff3b30', color: 'white' }}
        >
          {errorMessage || 'Failed to submit inquiry. Please try again.'}
        </Alert>
      </Snackbar>
    </>
  );
};

export default InquiryModal;